import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDepartments = () => {
  const { token } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    icon: '',
    status: 'active'
  });

  const icons = ['Stethoscope', 'Heart', 'Baby', 'Scissors', 'FlaskConical', 'Scan', 'Siren', 'Pill', 'Brain', 'Bone', 'Eye', 'Activity'];

  const fetchDepartments = async () => {
    try {
      const response = await fetch(`${API}/api/admin/departments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Failed to fetch departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingDept 
        ? `${API}/api/admin/departments/${editingDept.id}`
        : `${API}/api/admin/departments`;
      
      const response = await fetch(url, {
        method: editingDept ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingDept ? 'Department updated' : 'Department created');
        setShowModal(false);
        setEditingDept(null);
        resetForm();
        fetchDepartments();
      } else {
        throw new Error('Operation failed');
      }
    } catch (error) {
      toast.error('Failed to save department');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this department?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/departments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Department deleted');
        fetchDepartments();
      }
    } catch (error) {
      toast.error('Failed to delete department');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image: '',
      icon: '',
      status: 'active'
    });
  };

  const openEditModal = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      description: dept.description,
      image: dept.image || '',
      icon: dept.icon || '',
      status: dept.status
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingDept(null);
    resetForm();
    setShowModal(true);
  };

  const filteredDepartments = departments.filter(dept =>
    dept.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
              <Building2 className="w-7 h-7 text-primary" />
              Departments Management
            </h1>
            <p className="text-slate-600">Manage hospital departments</p>
          </div>
          <Button onClick={openCreateModal} className="bg-primary hover:bg-primary-700" data-testid="add-department-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Department
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="department-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Departments Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full p-8 text-center text-slate-500">Loading...</div>
          ) : filteredDepartments.length === 0 ? (
            <div className="col-span-full p-8 text-center text-slate-500">No departments found</div>
          ) : (
            filteredDepartments.map((dept) => (
              <Card key={dept.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video bg-slate-100 relative">
                  {dept.image ? (
                    <img src={dept.image} alt={dept.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building2 className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${dept.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                    {dept.status}
                  </span>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-800">{dept.name}</h3>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{dept.description}</p>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => openEditModal(dept)} data-testid={`edit-dept-${dept.id}`}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => handleDelete(dept.id)}
                      data-testid={`delete-dept-${dept.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingDept ? 'Edit Department' : 'Add Department'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Department Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      data-testid="department-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      rows={3}
                      required
                      data-testid="department-description-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="image">Image URL</Label>
                    <Input
                      id="image"
                      value={formData.image}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      placeholder="https://..."
                      data-testid="department-image-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="icon">Icon</Label>
                    <select
                      id="icon"
                      value={formData.icon}
                      onChange={(e) => setFormData({...formData, icon: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      data-testid="department-icon-select"
                    >
                      <option value="">Select Icon</option>
                      {icons.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="status">Status</Label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      data-testid="department-status-select"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="bg-primary hover:bg-primary-700" data-testid="department-save-btn">
                      <Save className="w-4 h-4 mr-2" />
                      {editingDept ? 'Update' : 'Create'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDepartments;
